/**
 * WorkOrderSettingsSection - Configures the per-organization link to an external
 * work order system (e.g. TMA iServiceDesk).
 *
 * For OrgAdmin users only. Phase 1: a configurable button/link shown on reports.
 * When enabled with a URL, end users see a button (labeled by `label`) that opens
 * the org's work order system. This same config is the foundation for the future
 * programmatic work-order submission.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { View, StyleSheet, Pressable, ActivityIndicator, TextInput } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { heightPercentageToDP as hp } from 'react-native-responsive-screen';

import ToggleSwitch from '../ToggleSwitch';
import CustomText from '../CustomText';
import AppStyles from '../../AppStyles';
import { useTranslation } from '../../i18n';
import {
  getWorkOrderConfig,
  saveWorkOrderConfig,
  normalizeWorkOrderUrl,
  isValidWorkOrderUrl,
  DEFAULT_WORK_ORDER_CONFIG,
} from '../../util/workOrderConfig';

const configEqual = (a, b) =>
  a.enabled === b.enabled && a.label === b.label && a.url === b.url;

/**
 * @param {string} organization - Organization name
 */
const WorkOrderSettingsSection = ({ organization }) => {
  const { t } = useTranslation(['admin', 'common']);

  const [config, setConfig] = useState({ ...DEFAULT_WORK_ORDER_CONFIG });
  const [savedConfig, setSavedConfig] = useState({ ...DEFAULT_WORK_ORDER_CONFIG });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const fetchConfig = useCallback(async () => {
    if (!organization) return;
    setLoading(true);
    setError(null);
    try {
      const fetched = await getWorkOrderConfig(organization);
      setConfig(fetched);
      setSavedConfig(fetched);
    } catch (err) {
      console.error('Error fetching work order config:', err);
      setError(t('workOrder.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [organization]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleChange = (key, value) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
  };

  const hasChanges = !configEqual(config, savedConfig);
  const trimmedUrl = (config.url || '').trim();
  // If enabled, a valid URL is required for the button to do anything useful.
  const urlMissing = config.enabled && !trimmedUrl;
  const urlInvalid = config.enabled && !!trimmedUrl && !isValidWorkOrderUrl(trimmedUrl);
  const labelMissing = config.enabled && !(config.label || '').trim();
  const canSave = hasChanges && !urlMissing && !urlInvalid && !labelMissing && !saving;

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const toSave = {
        ...config,
        label: (config.label || '').trim() || DEFAULT_WORK_ORDER_CONFIG.label,
        url: normalizeWorkOrderUrl(trimmedUrl),
      };
      await saveWorkOrderConfig(organization, toSave);
      setConfig(toSave);
      setSavedConfig(toSave);
    } catch (err) {
      console.error('Error saving work order config:', err);
      setError(err?.message || t('workOrder.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <View style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="small" color={AppStyles.colorSet.brandPrimary} />
          <CustomText style={styles.loadingText} size={14} color={AppStyles.colorSet.textSecondary}>
            {t('workOrder.loading')}
          </CustomText>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <CustomText style={styles.heading} font="semiBold" size={18} color={AppStyles.colorSet.textPrimary}>
        {t('workOrder.title')}
      </CustomText>
      <CustomText style={styles.explanationText} font="regular" size={14} color={AppStyles.colorSet.textSecondary}>
        {t('workOrder.description')}
      </CustomText>

      {error && (
        <View style={styles.inlineError}>
          <Feather name="alert-circle" size={14} color="#B91C1C" />
          <CustomText style={styles.inlineErrorText} size={13} color="#B91C1C">
            {error}
          </CustomText>
        </View>
      )}

      {/* Enable/Disable toggle */}
      <View style={styles.toggleRow}>
        <View style={{ flex: 1 }}>
          <CustomText font="medium" size={15} color={AppStyles.colorSet.textPrimary}>
            {t('workOrder.enable')}
          </CustomText>
          <CustomText size={12} color={AppStyles.colorSet.textMuted}>
            {t('workOrder.enableHint')}
          </CustomText>
        </View>
        <ToggleSwitch
          value={config.enabled}
          onValueChange={(val) => handleChange('enabled', val)}
        />
      </View>

      {config.enabled && (
        <View style={styles.sectionCard}>
          {/* Button label */}
          <CustomText style={styles.fieldLabel} font="medium" size={14} color={AppStyles.colorSet.textPrimary}>
            {t('workOrder.buttonLabel')}
          </CustomText>
          <TextInput
            style={styles.textInput}
            value={config.label}
            onChangeText={(text) => handleChange('label', text)}
            placeholder={t('workOrder.buttonLabelPlaceholder')}
            placeholderTextColor={AppStyles.colorSet.textMuted}
            maxLength={40}
          />
          {labelMissing && (
            <CustomText size={12} color="#D97706" style={styles.fieldHint}>
              {t('workOrder.labelRequired')}
            </CustomText>
          )}

          {/* URL */}
          <CustomText
            style={[styles.fieldLabel, { marginTop: 14 }]}
            font="medium"
            size={14}
            color={AppStyles.colorSet.textPrimary}
          >
            {t('workOrder.urlLabel')}
          </CustomText>
          <TextInput
            style={styles.textInput}
            value={config.url}
            onChangeText={(text) => handleChange('url', text)}
            placeholder={t('workOrder.urlPlaceholder')}
            placeholderTextColor={AppStyles.colorSet.textMuted}
            autoCapitalize="none"
            autoCorrect={false}
            keyboardType="url"
          />
          {urlMissing && (
            <CustomText size={12} color="#D97706" style={styles.fieldHint}>
              {t('workOrder.urlRequired')}
            </CustomText>
          )}
          {urlInvalid && (
            <CustomText size={12} color="#D97706" style={styles.fieldHint}>
              {t('workOrder.urlInvalid')}
            </CustomText>
          )}
        </View>
      )}

      <View style={styles.actionsRow}>
        <Pressable
          style={({ pressed, hovered, focused }) => [
            styles.saveButton,
            { cursor: 'pointer' },
            canSave && (hovered || focused) && styles.saveButtonHovered,
            !canSave && styles.buttonDisabled,
            pressed && styles.buttonPressed,
          ]}
          onPress={handleSave}
          disabled={!canSave}
        >
          {saving ? (
            <ActivityIndicator size="small" color="#000" />
          ) : (
            <>
              <Feather name="save" size={14} color="#000" />
              <CustomText style={{ marginLeft: 6 }} font="medium" size={14} color="#000">
                {t('workOrder.saveChanges')}
              </CustomText>
            </>
          )}
        </Pressable>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 4,
  },
  loadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: hp(3),
  },
  loadingText: {
    marginLeft: 10,
  },
  heading: {
    marginTop: hp(1),
    marginBottom: hp(0.5),
  },
  explanationText: {
    marginBottom: hp(1.5),
    lineHeight: 20,
  },
  inlineError: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FEF2F2',
    padding: 10,
    borderRadius: 8,
    marginBottom: hp(1),
  },
  inlineErrorText: {
    marginLeft: 8,
    flex: 1,
  },
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: AppStyles.colorSet.inputBackground,
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: '#E5E5E5',
    marginBottom: hp(1.5),
  },
  sectionCard: {
    backgroundColor: AppStyles.colorSet.inputBackground,
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: '#E5E5E5',
    marginBottom: hp(1.5),
  },
  fieldLabel: {
    marginBottom: 6,
  },
  fieldHint: {
    marginTop: 4,
  },
  textInput: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 15,
    color: AppStyles.colorSet.textPrimary,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E5E5',
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  actionsRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginTop: hp(0.5),
    marginBottom: hp(2),
  },
  saveButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: AppStyles.colorSet.brandPrimary,
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 8,
  },
  saveButtonHovered: {
    opacity: 0.92,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 6,
    elevation: 2,
  },
  buttonPressed: {
    opacity: 0.8,
  },
  buttonDisabled: {
    opacity: 0.5,
  },
});

export default WorkOrderSettingsSection;
